import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  activePreviewFlagCount,
  addDebugEntry,
  defineDebugAction,
  listDebugActions,
  listDebugEntries,
  listPreviewFlags,
  removeDebugEntry,
  removeDebugAction,
  setPreviewFlag,
  subscribeDebugActions,
  subscribeEntries,
  subscribePreviewFlags,
} from './state';
import { matchesDebugRoute } from './routeMatcher';
import type {
  DebugActionDefinition,
  DebugLayerEntry,
  PreviewFlagState,
} from './types';

interface DebugLayerContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
  /** Alle Entries (unfiltert) — meistens braucht man `routeEntries`. */
  entries: DebugLayerEntry[];
  /** Nur Entries, die für die aktuelle Route relevant sind. */
  routeEntries: DebugLayerEntry[];
  /** Alle Flags (unfiltert). */
  flags: PreviewFlagState[];
  /** Nur Flags, die für die aktuelle Route relevant sind. */
  routeFlags: PreviewFlagState[];
  /** Alle Actions (unfiltert). */
  actions: DebugActionDefinition[];
  /** Nur Actions, die für die aktuelle Route relevant sind. */
  routeActions: DebugActionDefinition[];
  activeFlagCount: number;
  /** Anzahl der Entries mit severity = warning|error. */
  warnErrorCount: number;
  /** Badge = nur route-relevante Warnings/Errors + aktive route-relevante Flags. */
  badgeCount: number;
  setFlag: (id: string, value: boolean) => void;
  registerEntry: (entry: DebugLayerEntry) => void;
  unregisterEntry: (id: string) => void;
  registerAction: (action: DebugActionDefinition) => void;
  unregisterAction: (id: string) => void;
  route: string;
}

const DebugLayerContext = createContext<DebugLayerContextValue | null>(null);

/**
 * Globaler State-Provider des Debug-Layers.
 *
 * Subscribt sich an die Module-Stores in `state.ts`, damit alle
 * Konsumenten (Floating-Button, Panel, externe Reporter) eine
 * konsistente, sub-100ms aktuelle Sicht haben.
 */
export function DebugLayerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [entryTick, setEntryTick] = useState(0);
  const [flagTick, setFlagTick] = useState(0);
  const [actionTick, setActionTick] = useState(0);

  // Initial bump deckt einen subtilen Mount-Race: Kind-Effekte (z. B.
  // `useRegisterImportDiagnostics`) laufen VOR den Effekten dieses
  // Providers und können bereits Entries/Actions in den Store
  // schreiben, bevor wir subskribieren. Ohne Bump bleibt der
  // useMemo-Snapshot auf dem leeren Initialwert hängen, bis der
  // nächste Store-Event kommt.
  useEffect(() => {
    setEntryTick((t) => t + 1);
    return subscribeEntries(() => setEntryTick((t) => t + 1));
  }, []);
  useEffect(() => {
    setFlagTick((t) => t + 1);
    return subscribePreviewFlags(() => setFlagTick((t) => t + 1));
  }, []);
  useEffect(() => {
    setActionTick((t) => t + 1);
    return subscribeDebugActions(() => setActionTick((t) => t + 1));
  }, []);

  const entries = useMemo(() => listDebugEntries(), [entryTick]);
  const flags = useMemo(() => listPreviewFlags(), [flagTick]);
  const actions = useMemo(() => listDebugActions(), [actionTick]);
  const pathname = location.pathname;
  const routeEntries = useMemo(
    () => entries.filter((e) => matchesDebugRoute(pathname, e.routeMatcher)),
    [entries, pathname],
  );
  const routeFlags = useMemo(
    () => flags.filter((f) => matchesDebugRoute(pathname, f.routeMatcher)),
    [flags, pathname],
  );
  const routeActions = useMemo(
    () => actions.filter((a) => matchesDebugRoute(pathname, a.routeMatcher)),
    [actions, pathname],
  );
  const activeFlagCount = useMemo(
    () => routeFlags.filter((f) => f.enabled).length,
    [routeFlags],
  );

  const warnErrorCount = useMemo(
    () =>
      routeEntries.filter(
        (e) => e.severity === 'warning' || e.severity === 'error',
      ).length,
    [routeEntries],
  );

  const badgeCount = activeFlagCount + warnErrorCount;

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const setFlag = useCallback(
    (id: string, value: boolean) => setPreviewFlag(id, value),
    [],
  );
  const registerEntry = useCallback(
    (entry: DebugLayerEntry) => addDebugEntry(entry),
    [],
  );
  const unregisterEntry = useCallback((id: string) => removeDebugEntry(id), []);
  const registerAction = useCallback(
    (action: DebugActionDefinition) => defineDebugAction(action),
    [],
  );
  const unregisterAction = useCallback(
    (id: string) => removeDebugAction(id),
    [],
  );

  const value = useMemo<DebugLayerContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      entries,
      routeEntries,
      flags,
      routeFlags,
      actions,
      routeActions,
      activeFlagCount,
      warnErrorCount,
      badgeCount,
      setFlag,
      registerEntry,
      unregisterEntry,
      registerAction,
      unregisterAction,
      route: pathname,
    }),
    [
      open,
      toggle,
      entries,
      routeEntries,
      flags,
      routeFlags,
      actions,
      routeActions,
      activeFlagCount,
      warnErrorCount,
      badgeCount,
      setFlag,
      registerEntry,
      unregisterEntry,
      registerAction,
      unregisterAction,
      pathname,
    ],
  );

  return (
    <DebugLayerContext.Provider value={value}>
      {children}
    </DebugLayerContext.Provider>
  );
}

export function useDebugLayer(): DebugLayerContextValue {
  const ctx = useContext(DebugLayerContext);
  if (!ctx) {
    throw new Error(
      'useDebugLayer must be used within <DebugLayerProvider>',
    );
  }
  return ctx;
}

/**
 * Hook-Variante zum kurzlebigen Registrieren eines Entries (z. B. von
 * einer Page, die einen Debug-Hinweis beisteuern will). Räumt beim
 * Unmount automatisch auf.
 */
export function useRegisterDebugEntry(
  entry: DebugLayerEntry | null | undefined,
): void {
  useEffect(() => {
    if (!entry) return;
    addDebugEntry(entry);
    const id = entry.id;
    return () => {
      removeDebugEntry(id);
    };
  }, [entry]);
}

/**
 * Registriert eine Admin-only Action im Debug-Layer. Räumt beim
 * Unmount automatisch auf. `execute` wird referenz-stabil als Capture
 * übernommen — Aufrufer sollten den Handler ggf. via `useCallback`
 * memoisieren, sonst wird die Action bei jedem Render neu registriert.
 */
export function useRegisterDebugAction(
  action: DebugActionDefinition | null | undefined,
): void {
  useEffect(() => {
    if (!action) return;
    defineDebugAction(action);
    const id = action.id;
    return () => {
      removeDebugAction(id);
    };
  }, [action]);
}
